import { DrinkScheduleBuilder } from "./schedule/drink.schedule.builder";
import { DocumentReference, DocumentData, AngularFirestore } from "@angular/fire/firestore";
import { MenuDrink } from "./menu.drink.model";
import { DrinkPositionBuilder } from "./position/drink.position.builder";
import { MenuCategory } from "../category/menu.category.model";
import { environment } from "src/environments/environment";

export class MenuDrinkBuilder {
  private _reference: DocumentReference;
  private _id: string;
  private _categories: DrinkPositionBuilder[];
  private _description: string;
  private _image: string;
  private _isActive: boolean;
  private _isEmergency: boolean;
  private _name: string;
  private _schedule: DrinkScheduleBuilder;
  private _isSponsor: boolean;
  private _sponsorText: string;
  constructor() {
    this.default();
  }

  public get reference(): DocumentReference {
    return this._reference;
  }
  public get hasReference(): boolean {
    return this._reference != null;
  }

  public get id(): string {
    return this._reference.id;
  }

  public get categories(): DrinkPositionBuilder[] {
    return this._categories;
  }

  public get name(): string {
    return this._name;
  }

  public set name(name: string) {
    this._name = name;
  }

  public get description(): string {
    return this._description;
  }

  public set description(desc: string) {
    this._description = desc;
  }

  public get image(): string {
    return this._image;
  }

  public set image(img: string) {
    this._image = img;
  }

  public get isDailyPrice(): boolean {
    return this._schedule.isDailyPrice;
  }

  public set isDailyPrice(b: boolean) {
    this._schedule.isDailyPrice = b;
  }

  public get price(): number {
    return this._schedule.price;
  }

  public get schedule(): DrinkScheduleBuilder {
    return this._schedule;
  }

  public get isActive(): boolean {
    return this._isActive;
  }

  public set isActive(b: boolean) {
    this._isActive = b;
  }

  public get isSponsor(): boolean {
    return this._isSponsor;
  }
  public set isSponsor(b: boolean) {
    this._isSponsor = b;
  }

  public get sponsorText(): string {
    return this._sponsorText;
  }
  public set sponsorText(s: string) {
    this._sponsorText = s;
  }

  public get isEmergency(): boolean {
    return this._isEmergency;
  }

  public set isEmergency(b: boolean) {
    this._isEmergency = b;
  }

  public loadAPIResponse(menuDrink: any, db: AngularFirestore): MenuDrinkBuilder {
      this._reference = db.doc(`${environment.drinks}/${menuDrink.id}`).ref;
      this._id = menuDrink.id;
      this._schedule = new DrinkScheduleBuilder().load(menuDrink.schedule);
      this._sponsorText = menuDrink.sponsorText ?? "";
      this._isSponsor = menuDrink.isSponsor ?? false;
      this._image = menuDrink.image;
      this._name = menuDrink.name;
      this._description = menuDrink.description;
      this._isActive = menuDrink.isActive;
      this._isEmergency = menuDrink.isEmergency ?? false;
      this._categories = menuDrink.categories;
      return this;
  }

  public toAPIResponse = (): any => ({
    id: this._id,
    categories: this._categories.map(obj => obj.toJSON()),
    description: this._description,
    image: this._image,
    name: this._name,
    schedule: this._schedule.toJSON(),
    isActive: this._isActive,
    sponsorText: this._sponsorText,
    isSponsor: this._isSponsor,
    isEmergency: this._isEmergency
  });

  public duplicate(original: MenuDrink): MenuDrinkBuilder {
    this._reference = null;
    this._isSponsor = original.isSponsor;
    this._sponsorText = original.sponsorText;
    this._schedule = new DrinkScheduleBuilder().load(original.schedule);
    this._image = original.image;
    this._description = original.description;
    this._isActive = original.isActive;
    this._isEmergency = original.isEmergency;
    this._categories = original.categories.map(pos =>
      new DrinkPositionBuilder().edit(pos)
    );
    return this;
  }

  default(): void {
    this._reference = null;
    this._categories = [];
    this._image = null;
    this._name = "";
    this._description = "";
    this._isActive = true;
    this._isSponsor = false;
    this._sponsorText = "";
    this._isEmergency = false;
    this._schedule = new DrinkScheduleBuilder();
  }

  edit(data: MenuDrink): MenuDrinkBuilder {
    this._reference = data.reference;
    this._schedule = new DrinkScheduleBuilder().edit(data.schedule);
    this._image = data.image;
    this._name = data.name;
    this._description = data.description;
    this._isActive = data.isActive;
    this._isEmergency = data.isEmergency;
    this._isSponsor = data.isSponsor;
    this._sponsorText = data.sponsorText;
    this._categories = data.categories.map(pos =>
      new DrinkPositionBuilder().edit(pos)
    );
    return this;
  }

  public hasCategory(category: MenuCategory): boolean {
    return this._categories.some(
      cat => cat.categoryId == category.id
    );
  }

  public addCategory(category: MenuCategory): void {
    this._categories.push(new DrinkPositionBuilder().create(category));
  }

  public updateCategories(selected: MenuCategory[]) {
    let keepAlive: DrinkPositionBuilder[] = [];
    //! MAINTAIN POSITIONS IN LISTS
    for (var i = 0; i < this.categories.length; i++)
      if (selected.some(v => v.id == this.categories[i].categoryId))
        keepAlive.push(this.categories[i]);
    //! WIPE POSITION LIST
    this._categories = [];
    for (var i = selected.length - 1; i >= 0; i--) {
      if (!keepAlive.some(s => s.categoryId == selected[i].id))
        this.addCategory(selected[i]);
    }
    keepAlive.forEach(v => this.categories.push(v));
  }

  build(): MenuDrink {
    return new MenuDrink(this);
  }
}
